import { getEntryPoint } from 'bookwithme-organizer';
import { getFirstTimeUserCheck, getOrganizerURL, TeachingMoment } from 'bookwithme-shared';
import classNames from 'owa-classnames';
import { observer } from 'owa-mobx-react';
import { logUsage } from 'owa-analytics';
import { isAnonymousUser } from 'bookings-shared/lib/c2/utils/isAnonymousUser';
import CalendarPersonFilled from 'owa-fluent-icons-svg/lib/icons/CalendarPersonFilled';
import React from 'react';

import { useBoolean } from '@fluentui/react-hooks';
import { CommandBarButton } from '@fluentui/react/lib/Button';
import { Icon } from '@fluentui/react/lib/Icon';

import {
    buttonRoot,
    iconColor,
    buttonTextContainer,
    myBookingsButton,
    horizontalContainer,
    myBookingsButtonIcon,
} from './BookWithMeSuiteHeader.scss';

import {
    bookwithmeAttendeeTeachingMomentButtonLabel,
    bookwithmeAttendeeTeachingMomentHeader,
    bookwithmeAttendeeTeachingMomentSummary,
} from './Shared.locstring.json';

const bookingButtonStyles = {
    root: buttonRoot,
    icon: iconColor,
    /* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
     * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
     *	> Function classNames called at module scope is not allowed. This may cause unwanted side effects. */
    textContainer: classNames(buttonTextContainer, myBookingsButton),
};
interface BookingButtonProps {
    buttonText: string;
}
export default observer(function BookingButton(props: BookingButtonProps) {
    const [isTeachingMomentOpen, { toggle: toggleTeachingMomentIsOpen }] = useBoolean(true);
    const [showTeachingMoment, setShowTeachingMoment] = React.useState(false);
    const toggleWithLog = React.useCallback(() => {
        logUsage('attendeeTeachingMomentClosed', {
            entryPoint: getEntryPoint(),
        });
        toggleTeachingMomentIsOpen();
    }, []);

    /* eslint-disable-next-line react-perf/jsx-no-new-function-as-prop  -- (https://aka.ms/OWALintWiki)
     * Baseline, please do not copy and paste this justification
     *	> JSX attribute values should not contain functions created in the same scope
     *	> JSX attribute values should not contain functions created in the same scope */
    const onBookingClick = () => {
        toggleWithLog();
        window.open(getOrganizerURL(), '_blank');
    };
    const bookingButtonElement = React.useRef<HTMLDivElement>(null);
    getFirstTimeUserCheck().then((result: boolean) => {
        if (result && !isAnonymousUser()) {
            // Show teaching bubble when c1 who hasn't onboarded yet tries to access c2 of some other user
            setShowTeachingMoment(true);
            logUsage('showAttendeeTeachingMoment', {
                entryPoint: getEntryPoint(),
            });
        }
    });
    return (
        <div className={classNames(horizontalContainer)} ref={bookingButtonElement}>
            <Icon
                className={classNames(myBookingsButton, myBookingsButtonIcon)}
                iconName={CalendarPersonFilled}
            />
            <CommandBarButton
                styles={bookingButtonStyles}
                text={props.buttonText}
                title={props.buttonText}
                aria-label={props.buttonText}
                onClick={onBookingClick}
            />
            {showTeachingMoment && (
                <TeachingMoment
                    onButtonClick={onBookingClick}
                    onDismiss={toggleWithLog}
                    teachingHeader={bookwithmeAttendeeTeachingMomentHeader}
                    teachingSummary={bookwithmeAttendeeTeachingMomentSummary}
                    teachingButtonLabel={bookwithmeAttendeeTeachingMomentButtonLabel}
                    target={bookingButtonElement}
                    isOpen={isTeachingMomentOpen}
                />
            )}
        </div>
    );
}, 'BookingButton');
